import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import type {
  ConnectedFacility,
  ConnectedFacilityEventHistory,
  ConnectedFacilityNote,
  PaginatedConnectedFacilityNoteList,
  PatchedConnectedFacilityEventUpdate,
  PatchedConnectedFacilityNote,
  PatchedConnectedFacilityStatusUpdate,
} from '../../../../../js/api';
import { OpenAPI, ConnectionsService, ProvidersService } from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { connectionRequestsQueryKeys } from '../../../../../js/app/query-keys-factories/connection-requests';
import { providerQueryKeys } from '../../../../../js/app/query-keys-factories/providers';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { Button } from '../../../components/button';
import { ConnectionRateHistoryTable } from '../../../components/connection-rate-history-table';
import { ContractSigningModal } from '../../../components/contract-signing-modal';
import { Dialog, DialogTitle, DialogBody } from '../../../components/dialog';
import { DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { ProviderExpenseAndTermsForm } from '../../../components/provider-expenses-and-terms-form';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsList, TabsButton, TabsContent } from '../../../components/tabs';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { EventCodes, StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type {
  ActivityEventHistory,
  ActivityHistory,
  ActivityStatusHistory,
} from '../../../utils/activity-types';
import { getStatusTextStyle } from '../../../utils/statusStyles';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ConnectionQuickDetails } from './ConnectionQuickDetails';
import { DetailsTab } from './DetailsTab';
import {
  CONNECTION_CHANGE_HISTORY_QUERY_KEY,
  CONNECTION_EVENT_HISTORY_QUERY_KEY,
  CONNECTION_NOTES_QUERY_KEY,
  CONNECTION_QUERY_KEY,
  CONNECTION_STATUS_HISTORY_QUERY_KEY,
  PROVIDER_CONNECTIONS_LIST_QUERY_KEY,
} from './queryKeys';
import { TravelTab } from './TravelTab';

const WORKFLOWS_META_QUERY_KEY = 'connections.connectedfacility';

type ConnectionDrawerProps =
  | {
      connection: ConnectedFacility;
      connectionId?: never;
      onClose?: () => void;
      prevId?: string | null;
      nextId?: string | null;
      selectedQueueCode?: string | undefined;
    }
  | {
      connection?: never;
      connectionId: string;
      onClose?: () => void;
      prevId?: string | null;
      nextId?: string | null;
      selectedQueueCode?: string | undefined;
    };

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ConnectionDrawer({
  connection,
  connectionId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
}: ConnectionDrawerProps) {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [pendingTargetStatus, setPendingTargetStatus] = useState<string | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Expense selection dialog (for PROV_ACCEPTED / "I'm Interested")
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);

  // Contract signing state
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractText, setContractText] = useState<string | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);
  const [isDownloadingContract, setIsDownloadingContract] = useState(false);

  // Fetch connection if only ID is provided
  const {
    data: fetchedConnection,
    isLoading: isLoadingConnection,
    error: connectionError,
  } = useQuery<ConnectedFacility>({
    queryKey: [CONNECTION_QUERY_KEY, connectionId],
    queryFn: () => ConnectionsService.connectionsConnectedFacilityRetrieve({ id: connectionId! }),
    enabled: !!connectionId && !connection,
  });

  // Fetch workflow metadata (standardized via hook)
  const { statuses: workflowStatuses } = useWorkflowMetadata(WORKFLOWS_META_QUERY_KEY);

  // Use provided connection or fetched connection
  const connectionData = connection || fetchedConnection;
  const effectiveConnectionId = connectionId || connectionData?.id;

  const tabs = useMemo(
    () => [
      { code: 'details', label: 'Details' as const },
      { code: 'travel', label: 'Travel' as const },
      { code: 'rate-history', label: 'Rate History' as const },
      { code: 'documents', label: 'Documents' as const },
    ],
    []
  );
  const [activeTab, setActiveTab] = useState<string>('details');

  // Reset active tab when switching connections
  useEffect(() => {
    if (effectiveConnectionId) setActiveTab('details');
  }, [effectiveConnectionId]);

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: effectiveConnectionId, tabs },
    });

  // Helper: after an action refreshes, if this item moved out of the current queue, auto-advance
  const checkAndNavigateIfRemoved = (updated?: ConnectedFacility) => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;

    const currentStatusCode =
      updated?.current_status?.code ?? connectionData?.current_status?.code ?? null;
    const queueName = getQueueNameForStatus(workflowStatuses as StatusMetadata, currentStatusCode);

    if (queueName && queueName !== selectedQueueCode) {
      const targetId = nextId || prevId || null;
      const nextParams = new URLSearchParams(searchParams);
      if (targetId) {
        nextParams.set('id', String(targetId));
        setSearchParams(nextParams, { replace: true });
      } else {
        nextParams.delete('id');
        setSearchParams(nextParams, { replace: true });
        onClose?.();
      }
    }
  };

  // Fetch notes
  const { data: notesData } = useQuery({
    queryKey: [CONNECTION_NOTES_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityNotesList({
        id: effectiveConnectionId!,
        ordering: '-created_at',
      }),
    enabled: !!effectiveConnectionId,
    select: (
      data: PaginatedConnectedFacilityNoteList | ConnectedFacilityNote | ConnectedFacilityNote[]
    ) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      if ('results' in data) {
        return data.results || [];
      }
      return [data as ConnectedFacilityNote];
    },
  });

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityStatusHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
  });

  // Fetch change history
  const { data: changeHistory } = useQuery({
    queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityChangeHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
  });

  // Fetch event history
  const { data: eventHistoryData } = useQuery({
    queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, effectiveConnectionId],
    queryFn: () =>
      ConnectionsService.connectionsConnectedFacilityEventHistoryRetrieve({
        id: effectiveConnectionId!,
        sort: 'desc',
      }),
    enabled: !!effectiveConnectionId,
    select: (data: ConnectedFacilityEventHistory | ConnectedFacilityEventHistory[]) => {
      if (!data) {
        return [];
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [data];
    },
  });

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      statusCode: string | null;
      reasonCode?: string | null;
      comment?: string;
    }) => {
      const statusUpdateData: PatchedConnectedFacilityStatusUpdate = {
        code: data.statusCode || undefined,
        reason_code: data.reasonCode ?? null,
        comment: data.comment || null,
      };
      return ConnectionsService.connectionsConnectedFacilityStatusUpdatePartialUpdate({
        id: data.id,
        requestBody: statusUpdateData,
      });
    },
    onSuccess: async (updatedConnection, variables) => {
      const { id } = variables;
      toast.success('Connection status successfully updated');
      queryClient.setQueryData([CONNECTION_QUERY_KEY, id], updatedConnection);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [PROVIDER_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_NOTES_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope and the global menu badges
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility],
        }),
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // If item moved out of current queue, navigate to next/prev or close
      checkAndNavigateIfRemoved(updatedConnection as ConnectedFacility);
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update connection status' });
    },
  });

  // Event update mutation (for event buttons)
  const eventUpdateMutation = useMutation({
    mutationFn: async (data: { id: string; eventCode: string }) => {
      const eventUpdateData: PatchedConnectedFacilityEventUpdate = {
        code: data.eventCode,
      };
      return ConnectionsService.connectionsConnectedFacilityEventUpdatePartialUpdate({
        id: data.id,
        requestBody: eventUpdateData,
      });
    },
    onSuccess: async (_updatedConnection, variables) => {
      const { id, eventCode } = variables;
      toast.success('Connection accepted successfully');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [CONNECTION_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [PROVIDER_CONNECTIONS_LIST_QUERY_KEY] }),
        queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_STATUS_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_CHANGE_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_EVENT_HISTORY_QUERY_KEY, id] }),
        queryClient.invalidateQueries({ queryKey: [CONNECTION_NOTES_QUERY_KEY, id] }),
        // Invalidate queue notification badges for this scope and the global menu badges
        queryClient.invalidateQueries({
          queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility],
        }),
        queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] }),
      ]);

      // For PROV_ACCEPTED ("I'm Interested"), show expense selection dialog after successful mutation
      if (eventCode === EventCodes.PROV_ACCEPTED) {
        setShowExpenseDialog(true);
      }

      // Fetch latest and check if it should auto-advance out of the current queue
      try {
        const latest = await ConnectionsService.connectionsConnectedFacilityRetrieve({ id });
        checkAndNavigateIfRemoved(latest as ConnectedFacility);
      } catch (_e) {
        // swallow; navigation not critical here
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to accept connection' });
    },
  });

  // Contract handling functions
  const handleContractRetrieveClick = useCallback(async () => {
    if (!user?.provider_id) return;

    setIsLoadingContract(true);
    setShowContractModal(true);

    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });
      setContractText(response.text);
    } catch (error) {
      console.error('Error retrieving contract:', error);
      handleApiError(error, { messagePrefix: 'Failed to load contract' });
    } finally {
      setIsLoadingContract(false);
    }
  }, [user?.provider_id]);

  const handleDownloadContract = useCallback(async () => {
    if (!user?.provider_id) return;
    try {
      setIsDownloadingContract(true);
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(
        `${OpenAPI.BASE}/api/providers/${user.provider_id}/contract-review-download/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-tz': xTz,
          },
        }
      );

      if (!response.ok) {
        console.error('Download failed:', response.statusText);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `unexecuted_sendit_professional_services_agreement.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsDownloadingContract(false);
    } catch (error) {
      console.error('Failed to download PDF', error);
      setIsDownloadingContract(false);
    }
  }, [user?.provider_id]);

  const handleSignContract = useCallback(
    async (firstName: string, lastName: string) => {
      if (!user?.provider_id) return;

      setIsSigningContract(true);
      try {
        await ProvidersService.providersContractCreate({
          id: user.provider_id,
          xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          requestBody: {
            signed_first_name: firstName,
            signed_last_name: lastName,
          },
        });

        toast.success('Professional Services Agreement signed successfully');
        setShowContractModal(false);
        setContractText(null);
        // User and current-provider caches both carry contract state; refresh both.
        queryClient.invalidateQueries({ queryKey: ['user', 'detail', 'current'] });
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.currentDetail() });
      } catch (error) {
        handleApiError(error, {
          messagePrefix: 'Failed to sign the Professional Services Agreement',
        });
      } finally {
        setIsSigningContract(false);
      }
    },
    [user?.provider_id, queryClient]
  );

  // Handle status modal confirm
  const handleStatusModalConfirm = async (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
  }) => {
    if (!effectiveConnectionId) {
      toast.error('Connection ID not found');
      return;
    }

    await statusUpdateMutation.mutateAsync({
      id: effectiveConnectionId,
      statusCode: data.statusCode,
      reasonCode: data.reasonCode,
      comment: data.comment,
    });
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  const handleStatusModalClose = () => {
    setIsStatusModalOpen(false);
    setPendingTargetStatus(null);
  };

  // Handle event buttons click
  const handleEventClick = async (eventCode: EventCodes) => {
    if (!effectiveConnectionId) {
      toast.error('Connection ID not found');
      return;
    }

    await eventUpdateMutation.mutateAsync({
      id: effectiveConnectionId,
      eventCode: eventCode,
    });
  };

  // Called when expense form saves successfully (from expense dialog) - close dialog only.
  // The PROV_ACCEPTED mutation already ran before this dialog was shown.
  const handleExpenseDialogSuccess = useCallback(() => {
    setShowExpenseDialog(false);
  }, []);

  // Relevant status check constants
  const isPending = connectionData?.current_status?.code === StatusCodes.REQUESTED;
  const isProvActionReq = connectionData?.current_status?.code === StatusCodes.PROV_ACTION_REQ;

  const needsContract = user?.needs_contract_signed || false;
  const canAccept = !isPending || !needsContract;

  // Find the most recent status update matching the current status and extract its note/comment
  const statusHistoryList = Array.isArray(statusHistory)
    ? (statusHistory as ActivityStatusHistory[])
    : statusHistory
      ? [statusHistory as ActivityStatusHistory]
      : [];
  const latestMatchingStatusUpdate = statusHistoryList.find(
    (h) => h?.to_status?.code === connectionData?.current_status?.code
  );
  const provActionReqNoteText =
    latestMatchingStatusUpdate?.note?.comment ?? latestMatchingStatusUpdate?.comment ?? '';

  // Check if contract alert should be shown
  const showContractAlert =
    needsContract && connectionData?.current_status?.code === StatusCodes.REQUESTED;

  if (isLoadingConnection) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">Loading connection details...</div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-stoplight-danger">
          Error loading connection details. Please try again.
        </div>
      </div>
    );
  }

  if (!connectionData) {
    return null;
  }

  return (
    <>
      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        initialStatusCode={connectionData?.current_status?.code || null}
        targetStatusCode={pendingTargetStatus}
        workflowScope="connections.connectedfacility"
        isPending={statusUpdateMutation.isPending}
        currentReasonCode={connectionData?.current_reason?.code || null}
        currentStatusTitle={connectionData?.current_status?.title || null}
      />
      <ContractSigningModal
        isOpen={showContractModal}
        onClose={() => {
          setShowContractModal(false);
          setContractText(null);
        }}
        contractText={contractText}
        isLoadingContract={isLoadingContract}
        isSigningContract={isSigningContract}
        isDownloadingContract={isDownloadingContract}
        onDownloadContract={handleDownloadContract}
        onSignContract={handleSignContract}
      />
      <Dialog open={showExpenseDialog} onClose={() => setShowExpenseDialog(false)} size="lg">
        <div className="tw:flex tw:items-start tw:justify-between tw:mb-4">
          <DialogTitle>Expected Travel Expenses</DialogTitle>
          <button
            onClick={() => setShowExpenseDialog(false)}
            className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:rounded"
            type="button"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark tw:text-lg" />
          </button>
        </div>
        <DialogBody>
          <div className="tw:font-medium">{connectionData.facility?.name}</div>
          <div className="tw:mb-4 tw:text-sm tw:text-zinc-600 tw:dark:text-zinc-400 tw:whitespace-pre-line">
            {[
              connectionData.facility?.street_address,
              connectionData.facility?.street_address2,
              [
                connectionData.facility?.city,
                connectionData.facility?.state,
                connectionData.facility?.zip_code,
              ]
                .filter(Boolean)
                .join(', '),
            ]
              .filter(Boolean)
              .join('\n') || '--'}
          </div>
          <ProviderExpenseAndTermsForm
            connection={connectionData}
            onUpdate={handleExpenseDialogSuccess}
          />
        </DialogBody>
      </Dialog>
      <Dialog open={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} size="lg">
        <div className="tw:flex tw:items-start tw:justify-between tw:mb-4">
          <DialogTitle>Connection Agreement</DialogTitle>
          <button
            onClick={() => setIsInfoModalOpen(false)}
            className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:rounded"
            type="button"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark tw:text-lg" />
          </button>
        </div>
        <DialogBody>
          <h5 className="tw:text-lg tw:font-semibold tw:mb-2 tw:text-zinc-950 tw:dark:text-white">
            {connectionData?.facility?.name}
          </h5>
          <div className="tw:mt-2">
            <p className="tw:mb-2">
              Accepting the connection indicates you are interested in sharing your contact
              information and discussing the opportunity further but does not guarantee commitment
              by either party.
            </p>
            <p className="tw:mb-2">Next steps will include:</p>
            <ul className="tw:list-disc tw:pl-6 tw:mb-4">
              <li className="tw:pb-2.5">
                The organization verifying that you do not currently have a relationship
              </li>
              <li className="tw:pb-2.5">The facility reviewing your profile for approval</li>
              <li className="tw:pb-2.5">
                A call with the Clinical Leader where you can ask questions and share what makes you
                a great fit
              </li>
            </ul>
          </div>
          <div>
            <p className="tw:mb-2">
              By accepting you <b>acknowledge and authorize</b>
            </p>
            <ul className="tw:list-disc tw:pl-6">
              <li className="tw:pb-2.5">Initial name clearance process</li>
              <li className="tw:pb-2.5">Facility presentation (once clearance received)</li>
              <li className="tw:pb-2.5">
                Any shifts worked as a result of this connection will be compensated at the rates
                listed
              </li>
            </ul>
          </div>
        </DialogBody>
      </Dialog>
      <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6">
        {/* Top Section - Facility Name, HCO, Status, Close */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
          {/* Hospital Icon - hidden on mobile */}
          <i className="tw:!hidden tw:md:!block fa-solid fa-hospital tw:text-brand-green-0 tw:text-3xl tw:shrink-0 tw:md:order-1" />
          {/* Facility Name and HCO */}
          <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:order-2">
            <span className="tw:text-base tw:md:text-xl tw:font-bold">
              {connectionData.facility?.name}
            </span>
            {connectionData.healthcare_entity?.name && (
              <span className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400 tw:truncate">
                {connectionData.healthcare_entity.name}
              </span>
            )}
          </div>
          {/* Status and Reason - always on its own row below header, left-aligned */}
          <div className="tw:order-4 tw:w-full tw:flex tw:flex-col tw:items-start tw:gap-2">
            <StatusPill
              scope={WorkflowScopes.ConnectedFacility}
              status={connectionData.current_status}
            />
            {connectionData.current_reason && (
              <div
                className={twMerge('tw:text-xs', getStatusTextStyle(connectionData.current_status))}
              >
                {connectionData.current_reason.title}
              </div>
            )}
            {connectionData.current_status.code === StatusCodes.PROV_ACTION_REQ && (
              <div className="tw:w-full tw:border-y tw:border-red-200 tw:text-stoplight-danger tw:py-2 tw:leading-4">
                <div className="tw:whitespace-pre-line">{provActionReqNoteText}</div>
                <div className="tw:text-xs tw:italic tw:text-zinc-600">
                  <br />
                  <a
                    href="/app/provider/edit"
                    className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:underline tw:font-medium"
                  >
                    Edit your profile
                  </a>{' '}
                  and upload requested documents and then return here and mark the action completed.
                  You may also enter comments below.
                </div>
              </div>
            )}
          </div>
          {/* Close Button - right side of row 1 */}
          {onClose && <DrawerCloseButton onClick={onClose} className="tw:order-3" />}
        </div>

        {/* Facility Details Section */}
        <ConnectionQuickDetails connection={connectionData} />

        {/* Contract Signing Alert */}
        {showContractAlert && (
          <div className="tw:mt-4 tw:p-3 tw:bg-yellow-50 tw:border tw:border-yellow-200 tw:rounded-lg tw:text-sm">
            <div className="tw:text-yellow-800">
              You must sign the Professional Services Agreement in order to move forward with this
              request.{' '}
              <button
                onClick={handleContractRetrieveClick}
                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:underline tw:font-medium tw:bg-transparent tw:border-0 tw:cursor-pointer"
              >
                Sign now
              </button>
            </div>
          </div>
        )}

        {/* Status Transition Buttons */}
        <div className="tw:flex tw:flex-wrap tw:justify-end tw:items-center tw:gap-2 tw:mt-4">
          {isPending && (
            <>
              <button
                onClick={() => setIsInfoModalOpen(true)}
                className="tw:text-brand-green-1 tw:hover:brand-green-0 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:rounded tw:p-1 tw:transition-colors"
                type="button"
                aria-label="Show connection information"
              >
                <i className="fa-regular fa-circle-question tw:text-lg" />
              </button>
              <Button
                color="brandgreen0"
                aria-label="I'm Interested"
                onClick={() => handleEventClick(EventCodes.PROV_ACCEPTED)}
                disabled={!canAccept || eventUpdateMutation.isPending}
                small={isMobile}
              >
                {eventUpdateMutation.isPending ? 'Processing...' : "I'm Interested"}
              </Button>
            </>
          )}
          {isProvActionReq && (
            <Button
              color="brandgreen0"
              aria-label="Mark task complete"
              onClick={() => handleEventClick(EventCodes.TASK_COMPLETED)}
              disabled={eventUpdateMutation.isPending}
              small={isMobile}
            >
              {eventUpdateMutation.isPending ? 'Marking task complete...' : 'Action Completed'}
            </Button>
          )}
          <StatusTransitionButtons
            isLoading={isLoadingConnection}
            currentStatusCode={connectionData?.current_status?.code}
            workflowMetadata={workflowStatuses as StatusMetadata}
            onStatusClick={(status) => {
              setPendingTargetStatus(status.code);
              setIsStatusModalOpen(true);
            }}
            isPending={statusUpdateMutation.isPending}
            small={isMobile}
          />
        </div>

        {/* Tabs Section */}
        <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-4">
          <div className="tw:flex tw:flex-col tw:w-full">
            {isOverflowCollapsed ? (
              <div className="tw:pb-2" data-testid="provider-connection-drawer-section-dropdown">
                <div className="tw:text-xs tw:text-zinc-500 tw:mb-1 tw:pl-1">Select View</div>
                <DropdownSelect
                  inputId="provider-connection-drawer-section"
                  aria-label="Connection section"
                  allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                  value={activeTab}
                  isSearchable={false}
                  isClearable={false}
                  onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                    if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                  }}
                />
              </div>
            ) : null}

            <div
              aria-hidden={isOverflowCollapsed}
              ref={tabsMeasureRef}
              className={twMerge(
                'tw:w-full',
                isOverflowCollapsed &&
                  'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
              )}
            >
              <TabsList
                className={twMerge(
                  'tw:border-b tw:border-zinc-200 tw:dark:border-zinc-700',
                  isOverflowCollapsed && 'tw:border-transparent tw:dark:!border-transparent'
                )}
              >
                {tabs.map((t) => (
                  <TabsButton key={t.code} tab={t.code}>
                    {t.label}
                  </TabsButton>
                ))}
              </TabsList>
            </div>
          </div>

          <TabsContent tab="details">
            <DetailsTab connection={connectionData} />
          </TabsContent>

          <TabsContent tab="rate-history">
            <ConnectionRateHistoryTable rateHistory={connectionData.rate_history || []} />
          </TabsContent>

          <TabsContent tab="travel">
            <TravelTab connection={connectionData} />
          </TabsContent>

          <TabsContent tab="documents">
            <DocumentsTab
              contextType="connections.connectedfacility"
              contextId={effectiveConnectionId!}
              ownerId={user?.provider_id ?? ''}
            />
          </TabsContent>
        </Tabs>

        <div className="tw:mt-2 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
          <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
          <ActivityLog
            enableCommentsFilter={true}
            enableStatusChangesFilter={true}
            enableUpdatesFilter={true}
            enableEventsFilter={true}
            notes={notesData}
            statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
            changeHistory={changeHistory as ActivityHistory[] | undefined}
            eventHistory={eventHistoryData as ActivityEventHistory[] | undefined}
            composerPlaceholder="Enter a comment (visible to all parties)..."
            noteMutations={{
              createNote: async (data) => {
                return ConnectionsService.connectionsConnectedFacilityNotesCreate({
                  id: effectiveConnectionId!,
                  requestBody: {
                    connected_facility: effectiveConnectionId!,
                    comment: data.comment,
                  } as ConnectedFacilityNote,
                });
              },
              updateNote: async (noteId: string, data: PatchedConnectedFacilityNote) => {
                return ConnectionsService.connectionsConnectedFacilityNotePartialUpdate({
                  id: noteId,
                  requestBody: data,
                });
              },
              deleteNote: async (noteId: string) => {
                return ConnectionsService.connectionsConnectedFacilityNoteDestroy({
                  id: noteId,
                });
              },
              notesQueryKey: [CONNECTION_NOTES_QUERY_KEY, effectiveConnectionId],
              extraInvalidateQueryKeys: [
                [CONNECTION_STATUS_HISTORY_QUERY_KEY, effectiveConnectionId],
              ],
            }}
          />
        </div>
      </div>
    </>
  );
}
